import { IgPaper, IgTypography } from "@intelligems/ig-theme";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";
import { Skeleton, Stack } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";

import { formatMetricValue } from "../../shared/utils/mobileAnalyticsFormatters";

type Props = {
  experienceTotals: {
    visitors: number;
    orders: number;
    revenue: number;
  };
  isLoading: boolean;
};

type CurrencyConfig = {
  code: string;
  locale: string;
};

const defaultConfig: CurrencyConfig = {
  code: "USD",
  locale: "en-US",
};

const DataCard = ({
  name,
  value,
  isCurrency,
  currencyConfig,
  isLoading,
}: {
  name: string;
  value: number;
  isCurrency: boolean;
  currencyConfig: CurrencyConfig;
  isLoading: boolean;
}) => {
  const formattedValue = formatMetricValue(
    value,
    isCurrency ? "currency" : "numberNoDecimal",
    currencyConfig
  );

  return (
    <IgPaper sx={{ flex: 1, p: 4 }}>
      <Stack justifyContent="center">
        <IgTypography variant={"body2"}>
          {capitalizeFirstLetter(name)}
        </IgTypography>
        <IgTypography variant={"body1"} fontWeight={600}>
          {isLoading ? (
            <Skeleton variant="rectangular" sx={{ borderRadius: "5px" }} />
          ) : (
            formattedValue
          )}
        </IgTypography>
      </Stack>
    </IgPaper>
  );
};

export const OverviewMetrics = ({ experienceTotals, isLoading }: Props) => {
  const config = useConfiguration<CurrencyConfig>("shop_currency");
  const currencyConfig = config?.value ?? defaultConfig;

  return (
    <Stack direction="row" sx={{ width: "100%" }} spacing={4}>
      <DataCard
        name="Orders"
        value={experienceTotals.orders}
        isLoading={isLoading}
        isCurrency={false}
        currencyConfig={currencyConfig}
      />
      <DataCard
        name="Visitors"
        value={experienceTotals.visitors}
        isLoading={isLoading}
        isCurrency={false}
        currencyConfig={currencyConfig}
      />
      <DataCard
        name="Revenue"
        value={experienceTotals.revenue}
        isLoading={isLoading}
        isCurrency={true}
        currencyConfig={currencyConfig}
      />
    </Stack>
  );
};
